'use client';

export type DocType = 'privacy_policy' | 'terms_of_service' | 'dispute_resolution';

export interface LegalDocument {
  type: DocType;
  title: string;
  content: string;
  last_updated: string;
  version: number;
}

const INITIAL_DOCS: Record<DocType, LegalDocument> = {
  privacy_policy: {
    type: 'privacy_policy',
    title: 'Privacy Policy',
    content: `# Privacy Policy

**Version 1.1 | Last Updated: May 2026**

WinBounty is operated by $30 bonus. This Privacy Policy explains how we collect, use, and protect your information when you use winbounty.io.

## Information We Collect

We collect information you provide directly, including:
- Identity and contact information (name, email, country)
- Financial information (Wise tag, encrypted at rest)
- Application responses and submission content
- Account credentials

We also collect automatically: log data, cookies, and usage analytics.

## How We Use Your Information

- To operate and improve the WinBounty platform
- To process bounty submissions and payments
- To communicate with you about your account
- To comply with legal obligations

We do not sell your personal information.

## Data Retention

- Active account data: retained while account is active
- Inactive accounts: 24 months after last login
- Financial records: 7 years
- Submission files: while account active + 12 months after closure

## Your Rights

You have the right to access, correct, delete, restrict processing, and receive a portable copy of your data. Contact privacy@winbounty.io to exercise these rights.

## Contact

$30 bonus | privacy@winbounty.io`,
    last_updated: '2026-05-01',
    version: 1,
  },
  terms_of_service: {
    type: 'terms_of_service',
    title: 'Terms of Service',
    content: `# Terms of Service

**Last Updated: May 2026**

These Terms govern your use of winbounty.io, operated by $30 bonus.

## Eligibility

You must be 18+, legally eligible to work, and pass our qualifying form to participate.

## Bounty Rules

- Each bounty requires exactly 3 video variations submitted as one batch
- Admins may request up to 3 revisions per submission
- All 3 variations must be approved for the $30+ reward to be paid

## Payments

- Approved bounties pay $30+ USD, added to your balance immediately
- Payouts are made monthly on the 25th via Wise only
- You are responsible for your own taxes

## Intellectual Property

Upon approval and payment, all rights to your submission transfer irrevocably to $30 bonus and its brands including Stepprs.

## Prohibited Conduct

You must not submit non-original work, use automation tools, attempt fraud, or violate applicable laws.

## Termination

Accounts may be terminated for any reason. Unpaid balances may be forfeited on termination for cause.

## Governing Law

Wyoming, USA.`,
    last_updated: '2026-05-01',
    version: 1,
  },
  dispute_resolution: {
    type: 'dispute_resolution',
    title: 'Dispute Resolution Policy',
    content: `# Dispute Resolution Policy

**Last Updated: May 2026**

This policy covers disputes between users and WinBounty regarding submission decisions, payments, and account actions.

## Scope

Covered: submission revisions/failures, bounty payments, account actions, XP/tier disputes.
Not covered: disputes between members.

## Process

1. **First step**: Contact the reviewing admin via in-app messaging
2. **Formal dispute**: Email disputes@winbounty.io within 30 days of the contested action
3. Include: your username, bounty/payment details, issue description, and evidence

## Timeline

- Acknowledgment: within 2 business days
- Investigation: within 10 business days (up to 30 for complex cases)

## Outcomes

- Submission reversal with credit and XP
- Payment correction
- Account reinstatement
- No action (dispute denied)

## Finality

Decisions are final within WinBounty. Dissatisfied users may pursue legal remedies under Wyoming law.

## Good Faith

Frivolous or fraudulent disputes may result in account suspension or termination.`,
    last_updated: '2026-05-01',
    version: 1,
  },
};

// Module-level store (persists across client-side navigation within a session)
let _docs: Record<DocType, LegalDocument> = { ...INITIAL_DOCS };

export const legalContentStore = {
  getAll(): Record<DocType, LegalDocument> {
    return _docs;
  },
  get(type: DocType): LegalDocument {
    return _docs[type];
  },
  set(type: DocType, doc: LegalDocument) {
    _docs = { ..._docs, [type]: doc };
  },
  setAll(docs: Record<DocType, LegalDocument>) {
    _docs = { ...docs };
  },
};
